import { onBeforeUnmount, ref, type Ref } from 'vue'
import { streamAIResponse } from '@/utils/ai-stream'
import { useHostServices } from '@/composables/format-edit/useHostServices'

export type StartAIStreamOptions = {
  /** @deprecated prefer resultRef / viewRef */
  resultField?: string
  viewField?: string
  resultRef?: Ref<string>
  viewRef?: Ref<boolean>
  onDone?: (data: unknown) => void
  onError?: (error: { message?: string }) => void
}

type FieldHost = Record<string, unknown>

/**
 * ai-streaming-mixin の Composition 版。
 * resultRef/viewRef を渡すか、host + resultField/viewField で代入する。
 */
export function useAiStreaming(host?: FieldHost) {
  const { message } = useHostServices()

  const aiIsStreaming = ref(false)
  const aiIsThinking = ref(false)
  const aiThinkingLabel = ref('準備中')
  const aiReasoningText = ref('')
  const aiShowReasoning = ref(false)
  const streamController = ref<{ abort: () => void } | null>(null)

  function resolveResultRef(options: StartAIStreamOptions): Ref<string> {
    if (options.resultRef) return options.resultRef
    const field = options.resultField || 'aiResultMyprResult'
    if (host && field in host) {
      return host[field] as Ref<string>
    }
    throw new Error(`useAiStreaming: missing result ref for ${field}`)
  }

  function resolveViewRef(options: StartAIStreamOptions): Ref<boolean> {
    if (options.viewRef) return options.viewRef
    const field = options.viewField || 'aiResultView'
    if (host && field in host) {
      return host[field] as Ref<boolean>
    }
    throw new Error(`useAiStreaming: missing view ref for ${field}`)
  }

  function setHostField(key: string, value: unknown) {
    if (!host || !(key in host)) return
    const target = host[key]
    if (target && typeof target === 'object' && 'value' in (target as object)) {
      ;(target as Ref<unknown>).value = value
    } else {
      host[key] = value
    }
  }

  function startAIStream(
    endpointUrl: string,
    formData: FormData,
    options: StartAIStreamOptions = {}
  ) {
    const resultRef = resolveResultRef(options)
    const viewRef = resolveViewRef(options)

    resultRef.value = ''
    viewRef.value = true
    aiIsStreaming.value = true
    aiIsThinking.value = true
    aiThinkingLabel.value = '準備中'
    aiReasoningText.value = ''
    aiShowReasoning.value = false
    setHostField('fullscreenLoading', false)

    let fullText = ''

    streamController.value = streamAIResponse(endpointUrl, formData, {
      onThinking: () => {
        aiIsThinking.value = true
        aiThinkingLabel.value = '思考中'
      },
      onReasoning: (content: string) => {
        aiThinkingLabel.value = '思考中'
        aiReasoningText.value += content
      },
      onText: (content: string) => {
        aiIsThinking.value = false
        fullText += content
        resultRef.value += content.replace(/\n/g, '<br>')
      },
      onDone: (data: { full_text?: string }) => {
        aiIsStreaming.value = false
        aiIsThinking.value = false
        if (data.full_text) {
          fullText = data.full_text
          resultRef.value = fullText.replace(/\n/g, '<br>')
        }
        message({ message: '取得しました', type: 'success' })
        options.onDone?.(data)
      },
      onError: (error: { message?: string }) => {
        aiIsStreaming.value = false
        aiIsThinking.value = false
        const errMessage = error.message || '失敗しました'
        if (errMessage === 'AIの使用回数が上限に達しました。') {
          message({
            message: '1日の上限に達しました。明日以降お試しください。',
            type: 'error'
          })
        } else {
          message({ message: errMessage, type: 'error' })
        }
        viewRef.value = false
        options.onError?.(error)
      }
    })
  }

  function toggleAiReasoning() {
    aiShowReasoning.value = !aiShowReasoning.value
  }

  function cancelAIStream() {
    if (streamController.value) {
      streamController.value.abort()
      streamController.value = null
      aiIsStreaming.value = false
      aiIsThinking.value = false
    }
  }

  onBeforeUnmount(() => {
    cancelAIStream()
  })

  return {
    aiIsStreaming,
    aiIsThinking,
    aiThinkingLabel,
    aiReasoningText,
    aiShowReasoning,
    startAIStream,
    toggleAiReasoning,
    cancelAIStream
  }
}
